import React, {useState, useEffect} from 'react';
import {clasesAPI, alumnosAPI, clasesAlumnosAPI} from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';
import ErrorMessage from '../Common/ErrorMessage';
import SearchFilter from '../Common/SearchFilter';
import {CheckIcon, XMarkIcon} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {getDayName, formatTime, filterBySearchTerm} from '../../services/utils';
import {useAuth} from '../../context/AuthContext';

const ClassAssignment = ({currentYear}) => {
    const {isReadOnly} = useAuth();
    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState(null);
    const [students, setStudents] = useState([]);
    const [classStudents, setClassStudents] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [classSearchTerm, setClassSearchTerm] = useState('');
    const [filteredClasses, setFilteredClasses] = useState([]);
    const [showOnlyAssigned, setShowOnlyAssigned] = useState(false);

    useEffect(() => {
        fetchData();
    }, [currentYear]);

    useEffect(() => {
        if (selectedClass) {
            loadClassStudents(selectedClass.IDClase);
        }
    }, [selectedClass, students]);

    useEffect(() => {
        filterStudents(searchTerm, showOnlyAssigned);
    }, [classStudents, showOnlyAssigned, students]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [classesRes, studentsRes] = await Promise.all(
                [clasesAPI.getByYear(currentYear), alumnosAPI.getByYear(currentYear)]
            );

            // Filtrar solo alumnos sin F_Baja_ME
            const activeStudents = studentsRes
                .data
                .filter(student => !student.F_Baja_ME);

            setClasses(classesRes.data);
            setFilteredClasses(filterClassesByTerm(classesRes.data, classSearchTerm));
            setStudents(activeStudents);
            setFilteredStudents(activeStudents);

            if (classesRes.data.length > 0) {
                const preservedSelectedClass = selectedClass
                    ? classesRes.data.find(clase => clase.IDClase === selectedClass.IDClase)
                    : null;
                setSelectedClass(preservedSelectedClass || classesRes.data[0]);
            } else {
                setSelectedClass(null);
            }

            setError(null);
        } catch (err) {
            setError('Error al cargar los datos');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadClassStudents = async (classId) => {
        try {
            const response = await clasesAlumnosAPI.getByClaseId(classId);
            const activeStudentIds = new Set(students.map(student => Number(student.IDAlumno)));
            const assignedActiveStudentIds = response.data
                .map(item => Number(item.AlumnoID))
                .filter(studentId => activeStudentIds.has(studentId));

            setClassStudents(assignedActiveStudentIds);
        } catch (err) {
            console.error('Error loading class students:', err);
        }
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        filterStudents(term, showOnlyAssigned);
    };

    const filterStudents = (searchTerm, onlyAssigned) => {
        let filtered = students;

        // Filtrar por asignación si está activado
        if (onlyAssigned) {
            filtered = filtered.filter(student => classStudents.includes(student.IDAlumno));
        }

        // Filtrar por término de búsqueda
        filtered = filterBySearchTerm(
            filtered,
            searchTerm,
            (student, searchTermLower) => student.Nombre.toLowerCase().includes(searchTermLower) || student.Apellido1.toLowerCase().includes(searchTermLower) || student.Apellido2
                ?.toLowerCase().includes(searchTermLower) || student.Email1.toLowerCase().includes(
                    searchTermLower
                )
        );

        setFilteredStudents(filtered);
    };

    const handleClassSearch = (term) => {
        setClassSearchTerm(term);
        setFilteredClasses(filterClassesByTerm(classes, term));
    };

    const filterClassesByTerm = (classesData, term) => {
        return filterBySearchTerm(
            classesData,
            term,
            (clase, searchTermLower) => clase.Asignatura.toLowerCase().includes(searchTermLower) || clase.Profesor.toLowerCase().includes(searchTermLower)
        );
    };

    const handleStudentToggle = async (studentId) => {
        const isAssigned = classStudents.includes(studentId);
        const updatedStudents = isAssigned
            ? classStudents.filter(id => id !== studentId)
            : [
                ...classStudents,
                studentId
            ];

        setClassStudents(updatedStudents);

        try {
            if (isAssigned) {
                await clasesAlumnosAPI.removeAlumnoDeClase(selectedClass.IDClase, studentId);
                toast.success('Alumno eliminado de la clase');
            } else {
                await clasesAlumnosAPI.addAlumnoAClase(selectedClass.IDClase, studentId);
                toast.success('Alumno asignado a la clase');
            }
        } catch (err) {
            toast.error('Error al actualizar la asignación');
            console.error(err);
            // Revertir en caso de error
            setClassStudents(classStudents);
        }
    };

    if (loading) 
        return <LoadingSpinner text="Cargando datos..."/>;
    if (error) 
        return <ErrorMessage message={error} onRetry={fetchData}/>;
    
    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Asignación de Alumnos a Clases</h1>
                <p className="text-gray-600">Curso {currentYear}/{currentYear + 1}</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Panel izquierdo - Selección de Clase */}
                <div className="lg:col-span-1 space-y-4">
                    <div className="card">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Seleccionar Clase</h2>
                        <div className="mb-4">
                            <SearchFilter
                                onSearch={handleClassSearch}
                                placeholder="Filtrar por Asignatura o Profesor..."
                                value={classSearchTerm}
                                onChange={(e) => handleClassSearch(e.target.value)}/>
                        </div>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                            {
                                filteredClasses.map(clase => (
                                    <button
                                        key={clase.IDClase}
                                        onClick={() => setSelectedClass(clase)}
                                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                                        selectedClass
                                            ?.IDClase === clase.IDClase
                                                ? 'bg-primary-50 border border-primary-200'
                                                : 'bg-gray-50 hover:bg-gray-100'}`}>
                                        <div className="font-medium text-gray-900">
                                            {clase.Asignatura} - {clase.Profesor}
                                        </div>
                                        <div className="text-sm text-gray-600">
                                            {getDayName(clase.DiaSemana)} - {formatTime(clase.H_Inicio)} - {formatTime(clase.H_Final)}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            Instrumento: {clase.Instrumento}
                                        </div>
                                    </button>
                                ))
                            }
                        </div>
                    </div>

                    {
                        selectedClass && (
                            <div className="card">
                                <h3 className="font-semibold text-gray-900 mb-2">Información de la Clase</h3>
                                <div className="space-y-2">
                                    <p>
                                        <span className="font-medium">Asignatura: </span>
                                        {selectedClass.Asignatura}</p>
                                    <p>
                                        <span className="font-medium">Instrumento: </span>
                                        {selectedClass.Instrumento}</p>
                                    <p>
                                        <span className="font-medium">Profesor: </span>
                                        {selectedClass.Profesor}</p>
                                    <p>
                                        <span className="font-medium">Horario: </span>
                                        {getDayName(selectedClass.DiaSemana)} - {formatTime(selectedClass.H_Inicio)} - {formatTime(selectedClass.H_Final)}</p>
                                    <p>
                                        <span className="font-medium">Alumnos asignados: </span>
                                        {classStudents.length}</p>
                                </div>

                            </div>
                        )
                    }
                </div>

                {/* Panel derecho - Lista de Alumnos */}
                <div className="lg:col-span-2">
                    <div className="card">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-gray-900">
                                { selectedClass? `Alumnos - ${selectedClass.Asignatura}`: 'Seleccione una clase' }
                            </h2>
                        </div>

                        <div className="mb-4 space-y-3">
                            <SearchFilter
                                onSearch={handleSearch}
                                placeholder="Buscar alumnos..."
                                value={searchTerm}
                                onChange={(e) => handleSearch(e.target.value)}/>
                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={showOnlyAssigned}
                                    onChange={(e) => setShowOnlyAssigned(e.target.checked)}
                                    className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"/>
                                <span className="text-sm text-gray-700">
                                    Mostrar sólo alumnos asignados ({classStudents.length})
                                </span>
                            </label>
                        </div>

                        {
                            selectedClass
                                ? (
                                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                                        {
                                            filteredStudents.map(student => {
                                                const isAssigned = classStudents.includes(student.IDAlumno);
                                                return (
                                                    <div
                                                        key={student.IDAlumno}
                                                        className={`flex items-center justify-between p-3 rounded-lg ${
                                                        isAssigned
                                                            ? 'bg-green-50 border border-green-200'
                                                            : 'bg-gray-50'}`}>
                                                        <div>
                                                            <div className="font-medium text-gray-900">
                                                                {student.Nombre} {student.Apellido1} {student.Apellido2}                                                      </div>
                                                            <div className="text-sm text-gray-600">
                                                                {student.Email1} • {student.Telefono1}
                                                            </div>
                                                        </div>
                                                        {
                                                            !isReadOnly() && (
                                                                <button
                                                                    onClick={() => handleStudentToggle(student.IDAlumno)}
                                                                    className={`p-2 rounded-full ${
                                                                    isAssigned
                                                                        ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                                    title={isAssigned? 'Remover de la clase' : 'Asignar a la clase'}>
                                                                    {
                                                                        isAssigned? (<XMarkIcon className="h-5 w-5"/>) : (<CheckIcon className="h-5 w-5"/>)
                                                                    }
                                                                </button>
                                                            )
                                                        }
                                                    </div>
                                                );
                                            })
                                        }
                                    </div>
                                )
                                : (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500">Seleccione una clase para ver y asignar alumnos</p>
                                    </div>
                                )
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClassAssignment;
